import React, { createContext, useContext, useEffect } from 'react';
import { useThemeStore, themeConfig, ThemeMode } from '../stores/themeStore';

interface ThemeContextType {
  currentTheme: typeof themeConfig.default;
  mode: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { mode, setTheme } = useThemeStore();
  const currentTheme = themeConfig[mode];

  // Apply theme to CSS variables
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary', currentTheme.primary);
    root.style.setProperty('--secondary', currentTheme.secondary);
    root.style.setProperty('--background', currentTheme.background);
    root.style.setProperty('--card', currentTheme.card);
    root.style.setProperty('--text', currentTheme.text);
    root.style.setProperty('--accent', currentTheme.accent);
    root.style.setProperty('--gradient-primary', currentTheme.gradients.primary);
    root.style.setProperty('--gradient-card', currentTheme.gradients.card);
    root.style.setProperty('--shadow-primary', currentTheme.shadows.primary);
    root.style.setProperty('--shadow-card', currentTheme.shadows.card);

    // Add theme class to body
    document.body.className = `theme-${mode}`;
  }, [mode, currentTheme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    localStorage.setItem('theme', mode);
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ currentTheme, mode, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 